import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  EmptyState,
  PageHeader,
  StatusBadge,
} from "../../components/dashboard/DashboardUI";
import { getTransactions } from "../../redux/actions/TransactionActions";
import { formatPrice } from "../../utils/formatPrice";

const PAGE_SIZE = 20;

// Payment statuses shown with the colours of the request statuses
const PAYMENT_STATUS = {
  completed: { badge: "fulfilled", label: "Réussi" },
  failed: { badge: "failed", label: "Échoué" },
  initiated: { badge: "initiated", label: "En attente" },
};

// Payments history; `staff` lists every payment of the platform
function UserTransactionHistory({
  title = "Paiements",
  lead = "L'historique de vos paiements sur Platform98.",
  staff = false,
}) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const { transactions } = useSelector((state) => state.transactions);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    Promise.resolve(dispatch(getTransactions(user.token))).finally(() =>
      setLoading(false),
    );
  }, [user?.token, dispatch]);

  const term = search.trim().toLowerCase();
  const rows = (transactions || [])
    .filter(
      (transaction) =>
        (!status || transaction.status === status) &&
        (!term ||
          transaction.reference?.toLowerCase().includes(term) ||
          transaction.phone?.includes(term) ||
          String(transaction.amount).includes(term)),
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <>
      <PageHeader title={title} lead={lead} />

      <div className="dash-card">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Rechercher une référence ou un montant"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setVisible(PAGE_SIZE);
            }}
            aria-label="Rechercher"
          />
          {staff && (
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setVisible(PAGE_SIZE);
              }}
              aria-label="Statut"
            >
              <option value="">Tous les statuts</option>
              {Object.entries(PAYMENT_STATUS).map(([value, item]) => (
                <option key={value} value={value}>
                  {item.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {loading ? (
          <p className="dash-loading">Chargement des paiements…</p>
        ) : rows.length === 0 ? (
          <EmptyState title="Aucun paiement">
            {transactions?.length
              ? "Aucun paiement ne correspond à votre recherche."
              : staff
                ? "Aucun paiement pour le moment."
                : "Vos paiements apparaîtront ici."}
          </EmptyState>
        ) : (
          <>
            <div className="dash-table-wrap">
              <table className="dash-table is-responsive">
                <thead>
                  <tr>
                    <th>Référence</th>
                    <th>Date</th>
                    <th>Moyen de paiement</th>
                    <th>Montant</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, visible).map((transaction) => {
                    const status = PAYMENT_STATUS[transaction.status];

                    return (
                      <tr key={transaction.reference}>
                        <td>
                          <span className="dash-cell-title">
                            #{transaction.reference}
                          </span>
                        </td>
                        <td data-label="Date">
                          {moment(transaction.createdAt).format("DD/MM/YYYY")}
                        </td>
                        <td data-label="Moyen">
                          {transaction.paymentMethod === "mobile_money"
                            ? `Mobile Money${
                                transaction.phone
                                  ? ` (${transaction.phone})`
                                  : ""
                              }`
                            : "Carte bancaire"}
                        </td>
                        <td data-label="Montant">
                          <strong>{formatPrice(transaction.amount)}</strong>
                        </td>
                        <td data-label="Statut">
                          <StatusBadge
                            status={status?.badge}
                            label={status?.label || "Inconnu"}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {rows.length > visible && (
              <div className="dash-more">
                <button
                  type="button"
                  className="dash-button is-secondary"
                  onClick={() => setVisible((count) => count + PAGE_SIZE)}
                >
                  Afficher plus
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default UserTransactionHistory;
